import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", dob: "", country: "US", consent: false, as_creator: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        if (!form.consent) {
          toast.error("You must confirm you are 18+");
          setBusy(false);
          return;
        }
        await register(form);
        toast.success("Account created");
      }
      nav("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 animate-fade-up">
      <div className="mb-8">
        <span className="uppercase tracking-[0.3em] text-xs text-gold">WhisperTales</span>
        <h1 className="font-display text-4xl mt-3">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
      </div>

      <div className="glass border rounded-xl p-7">
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Display name</Label>
              <Input data-testid="auth-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 bg-black/30 border-white/10" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
            <Input data-testid="auth-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5 bg-black/30 border-white/10" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
            <Input data-testid="auth-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={mode === "register" ? 8 : undefined} className="mt-1.5 bg-black/30 border-white/10" />
          </div>

          {mode === "register" && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date of birth</Label>
                <Input data-testid="auth-dob" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} required className="mt-1.5 bg-black/30 border-white/10" />
              </div>
              <label className="flex items-start gap-3 pt-1 cursor-pointer">
                <Checkbox data-testid="auth-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 border-white/20" />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I confirm I am 18 or older and consent to viewing mature, adult-oriented content.
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox data-testid="auth-creator" checked={form.as_creator} onCheckedChange={(v) => set("as_creator", !!v)} className="border-white/20" />
                <span className="text-xs text-muted-foreground">Sign up as a creator (upload & sell audio)</span>
              </label>
            </>
          )}

          <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90 shadow-[0_0_15px_rgba(155,27,48,0.3)] transition-colors duration-300">
            {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <button
          data-testid="auth-toggle"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 w-full text-center"
        >
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
